import { useEffect, useMemo, useState } from "react";
import { FlexCenterWrapper } from "../components/DefaultNoDataWrapper";
import Flex from "../components/Flex";
import Text from "../components/Text";
import CustomTitle from "../components/app/CustomTitle";
import { ENCOUNTER_FORM_MAPPER } from "../pages/app/provider/scheduling/appointment/encounter/content-section/VisitNoteForms";
import {
  NonNullableDate,
  formatDateTime,
  formatTime,
} from "../utils/date-time-utility";
import BirpNoteTemplate from "./birpNoteTemplate/BirpNoteTemplate";
import CollaborativeCareModelTemplate from "./collaborativeCareModelTemplate/CollaborativeCareModelTemplate";
import DapNoteTemplate from "./dapNoteTemplate/DapNoteTemplate";
import {
  KeyValPair,
  RenderTextWithContainerPattern,
  ReportContainer,
} from "./constant";
import IndividualTherapyNoteTemplate from "./individualTherapyNoteTemplate/IndividualTherapyNoteTemplate";
import InitialPsychEvolutionTemplate from "./initialPsychEvolutionTemplate/InitialPsychEvolutionTemplate";
import PreoperativeNoteTemplate from "./preoperativeNoteTemplate/PreoperativeNoteTemplate";
import PrimaryCareSoapNoteTemplate from "./primaryCareSoapNoteTemplate/PrimaryCareSoapNoteTemplate";
import ProgressNoteTemplate from "./progressNoteTemplate/ProgressNoteTemplate";
import PsychiatricNoteTemplate from "./psychiatricNoteTemplate/PsychiatricNoteTemplate";
import PsychiatricProgressTemplate from "./psychiatricProgressTemplate/PsychiatricProgressTemplate";
import PsychodynamicTherapyNoteTemplate from "./psychodynamicTherapyNoteTemplate/PsychodynamicTherapyNoteTemplate";
import PsychoeducationTherapyTemplate from "./psychoeducationTherapyTemplate/PsychoeducationTherapyTemplate";
import PsychotherapyNoteTemplate from "./psychotherapyNoteTemplate/PsychotherapyNoteTemplate";
import SoapNoteTemplate from "./soapNoteTemplate/SoapNoteTemplate";
import TreatmentPlanTemplate from "./treatmentPlanTemplate/TreatmentPlanTemplate";

interface EncounterSummeryTemplateProps {
  data: any;
  providerSignature?: string;
}

const fetchAsDataUrl = async (url: string): Promise<string | undefined> => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
};

/**
 * Resolves `data.providerGroupLogo` (an S3 URL) into an inline base64 data URL
 * so html2canvas can rasterize it during PDF generation. Returns `null` while
 * resolving; callers should defer triggering PDF generation until non-null.
 */
export const useResolveEncounterLogo = (data: any) => {
  const [resolved, setResolved] = useState<any>(null);

  useEffect(() => {
    if (!data) {
      setResolved(null);
      return;
    }
    const logoUrl = data.providerGroupLogo;
    if (!logoUrl || logoUrl.startsWith("data:")) {
      setResolved(data);
      return;
    }
    let cancelled = false;
    fetchAsDataUrl(logoUrl).then((dataUrl) => {
      if (cancelled) return;
      setResolved({ ...data, providerGroupLogo: dataUrl });
    });
    return () => {
      cancelled = true;
    };
  }, [data]);

  return resolved;
};

/**
 * Drops ROS/PE template entries whose description is blank/null/empty so that
 * only fields containing data (and their section headers) appear in the
 * Encounter Summary Report. See ECRH-5142.
 */
const filterEmptyTemplates = (templates: any) =>
  Array.isArray(templates)
    ? templates.filter((it) => !!it?.description?.trim())
    : templates;

const getTemplateByFormType = (data: any) => {
  switch (data?.formType) {
    case "TREATMENT_PLAN":
      return <TreatmentPlanTemplate data={data} />;
    case "PREOPERATIVE_NOTE":
      return <PreoperativeNoteTemplate data={data} />;
    case "PSYCHIATRIC_PROGRESS":
      return <PsychiatricProgressTemplate data={data} />;
    case "INITIAL_PSYCHOTHERAPY":
      return <InitialPsychEvolutionTemplate data={data} />;
    case "PSYCHIATRIC_NOTE":
      return <PsychiatricNoteTemplate data={data} />;
    case "PSYCHOTHERAPY_NOTE":
      return <PsychotherapyNoteTemplate data={data} />;
    case "PSYCHODYNAMIC_THERAPY_NOTE":
      return <PsychodynamicTherapyNoteTemplate data={data} />;
    case "PSYCHOEDUCATION_THERAPY":
      return <PsychoeducationTherapyTemplate data={data} />;
    case "INDIVIDUAL_THERAPY_NOTE":
      return <IndividualTherapyNoteTemplate data={data} />;
    case "COLLABORATIVE_CARE_MODEL":
      return <CollaborativeCareModelTemplate data={data} />;
    case "PRIMARY_CARE_SOAP_NOTE":
      return <PrimaryCareSoapNoteTemplate data={data} />;
    case "DAP_NOTE":
      return <DapNoteTemplate data={data} />;
    case "BIRP_NOTE":
      return <BirpNoteTemplate data={data} />;
    case "PROGRESS_NOTE":
      return <ProgressNoteTemplate data={data} />;
    case "SIMPLE_SOAP_NOTE":
    default:
      return <SoapNoteTemplate data={data} />;
  }
};

/**
 * EncounterSummeryTemplate — Router component that wraps common header/footer
 * around form-type-specific templates.
 *
 * @author [Rajkumar Rathod, Atish Bagate]
 */
/**
 * Print/PDF pagination rules — scoped to the encounter template root so every
 * form-type template (Psychodynamic, SOAP, etc.) inherits the same behavior.
 *
 * Strategy used (standard CSS Fragmentation, consumed by html2pdf.js / browser
 * print engines):
 *   1. Mark every sub-section card (a wrapper div whose first child is the
 *      styled SectionHead paragraph) as `page-break-inside: avoid`. The engine
 *      then pushes the whole card to the next page rather than slicing it
 *      mid-content. A small gap at the bottom of the previous page is the
 *      accepted trade-off; data is never split mid-word.
 *
 *      The exceptions are the top-level bands (`.report-section`) and the cards
 *      whose height follows the data (`.report-card--flow`, e.g. Prescription
 *      and CPT Code). html2pdf pushes any `break-inside: avoid` element that
 *      straddles a page boundary and is at most one page tall onto the next
 *      page, so marking a band that wraps a whole Plan — or a list that grows
 *      with the record — left up to a full page of white space behind it. Those
 *      continue across the break instead; their leaf rows stay atomic, so a
 *      line is still never split.
 *   2. Keep small leaf elements (<p>, <li>) atomic for the same reason at the
 *      sub-section level.
 *   3. Glue section headers to the first content row via `break-after: avoid`.
 *   4. Top/bottom padding on the root gives breathing room near page edges so
 *      content does not visually butt against the cut line.
 *
 * Note: `box-decoration-break: clone` was intentionally not used here. While
 * useful in theory for split sections, it caused empty bordered boxes to
 * appear at the top of pages whenever a parent wrapper spanned a page break
 * but its child content had already been pushed forward.
 */
const ENCOUNTER_PDF_PRINT_STYLES = `
  .encounter-pdf-root {
    padding-top: 12px;
    padding-bottom: 12px;
    orphans: 4;
    widows: 4;
  }
  /* Section cards: any div whose direct child is a styled SectionHead <p>,
     except the top-level bands and the data-driven lists. Those flow across
     pages instead of being bumped whole to the next one, which is what left
     the report with large blank areas before the Prescription and CPT Code
     blocks. The exclusions live in this selector rather than in a later
     override because the :has() selector outweighs a plain class rule. */
  .encounter-pdf-root
    div:has(> p[style*="background"]):not(.report-section):not(.report-card--flow) {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .encounter-pdf-root .report-section,
  .encounter-pdf-root .report-card--flow {
    page-break-inside: auto;
    break-inside: auto;
  }
  /* SubSection / NestedText outer wrappers (identified by border-radius in
     inline style). Required because their overflow:hidden creates a new
     block-formatting context that traps the inner card's break-inside rule
     and lets the outer border render on the previous page as an empty box. */
  .encounter-pdf-root div[style*="border-radius"] {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* Atomic leaf rows: key-value lines, list items, header paragraphs */
  .encounter-pdf-root p,
  .encounter-pdf-root li {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* Section header glued to first content row */
  .encounter-pdf-root p[style*="background"] {
    page-break-after: avoid;
    break-after: avoid;
  }
  .encounter-pdf-root h1,
  .encounter-pdf-root h2,
  .encounter-pdf-root h3,
  .encounter-pdf-root h4,
  .encounter-pdf-root h5,
  .encounter-pdf-root h6 {
    page-break-after: avoid;
    break-after: avoid;
  }
  @media print {
    .encounter-pdf-root
      div:has(> p[style*="background"]):not(.report-section):not(.report-card--flow) {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .encounter-pdf-root .report-section,
    .encounter-pdf-root .report-card--flow {
      page-break-inside: auto;
      break-inside: auto;
    }
  }
`;

const EncounterSummeryTemplate = ({
  data,
  providerSignature,
}: EncounterSummeryTemplateProps) => {
  // Strip empty ROS/PE fields before rendering the form-type template so blank
  // fields and their section headers are not populated in the report.
  const reportData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      rosTemplates: filterEmptyTemplates(data?.rosTemplates),
      physicalTemplates: filterEmptyTemplates(data?.physicalTemplates),
    };
  }, [data]);

  return (
    <div
      className="encounter-pdf-root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <style>{ENCOUNTER_PDF_PRINT_STYLES}</style>
      {/* ── Common Header ── */}
      <Flex
        width={"100%"}
        height={"100%"}
        alignItems={"center"}
        justifyContent={"center"}
        flexDirection={"column"}
        mb={2}
      >
        {data?.providerGroupLogo && (
          <img
            src={data?.providerGroupLogo}
            alt="provider group logo"
            style={{
              maxWidth: "120px",
              maxHeight: "60px",
              objectFit: "contain",
            }}
          />
        )}
        <FlexCenterWrapper>
          <CustomTitle>{data?.providerGroupName ?? "eCareHealth"}</CustomTitle>
        </FlexCenterWrapper>
      </Flex>

      {/* ── Visit Details ── */}
      <ReportContainer label="Visit Details">
        <div style={{ padding: "2px" }}>
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Id"
                v={data?.appointmentId?.split("-")?.[0]?.toUpperCase() ?? "-"}
              />
              <KeyValPair k="Patient Name" v={data?.patientName ?? "-"} />
              <KeyValPair
                k="Dob"
                v={NonNullableDate(data?.patientDob) ?? "-"}
              />
            </div>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Date"
                v={NonNullableDate(data?.encounterDate) ?? "-"}
              />
              <KeyValPair
                k="Appointment Date & Time"
                v={formatDateTime(data?.appointmentDate) ?? "-"}
              />
              <KeyValPair k="Provider Name" v={data?.providerName ?? "-"} />
            </div>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Note"
                v={
                  data?.formType ? ENCOUNTER_FORM_MAPPER?.[data?.formType] : "-"
                }
              />
              <KeyValPair
                k="Relationship To Insured"
                v={data?.insuredRelationshipWithPatientLabel ?? "-"}
              />
              <KeyValPair k="Insurance Name" v={data?.insuranceName ?? "-"} />
            </div>
          </div>
        </div>
      </ReportContainer>

      {/* ── Chief Complaint (common) — shown right after Visit Details for every
          note type so it always appears, independent of the note's own
          subjective chief complaint. */}
      <RenderTextWithContainerPattern
        label="Chief Complaint"
        val={reportData?.chiefComplaint}
      />

      {/* ── Form-Type Specific Template ──
          Screening Assessments are rendered by each form-type template inside
          its Objective section so the report mirrors where the screeners are
          captured in the encounter UI. */}
      {getTemplateByFormType(reportData)}

      {/* ── Common Signature Footer ── */}
      <ReportContainer label="Visit Signed by">
        <div
          style={{
            pageBreakInside: "avoid",
            breakInside: "avoid",
            padding: "2px",
          }}
        >
          <Text>{`Signed by ${data?.providerName} on ${NonNullableDate(
            data?.encounterDate
          )} at ${
            data?.encounterDate ? formatTime(data?.encounterDate) : "-"
          }`}</Text>
          <img
            src={providerSignature || data?.providerSignature}
            alt="signature"
            width={"120px"}
            height={"120px"}
            style={{
              display: "block",
              objectFit: "contain",
              pageBreakInside: "avoid",
              breakInside: "avoid",
            }}
          />
        </div>
      </ReportContainer>
    </div>
  );
};

export default EncounterSummeryTemplate;
